import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import DashboardCard from '/@/components/bayer/dashboard-card';
import type { UserNodeRecommendationsDashboardResponse } from '/@/hooks/dashboards';
import { _ } from '/@/locale';
import { getRecommendationTypes } from '../../hooks/crops';

export const MAX_PRODUCTS_SHOWN = 3;
const EMPTY_PLACEHOLDER_KEYS = ['empty-1', 'empty-2', 'empty-3'] as const;

export type RecommendationsProduct = NonNullable<
    UserNodeRecommendationsDashboardResponse['mostRecommendedProducts']
>[number];

export function MostRecommendedProductRow({
    product,
    recommendationTypes,
}: {
    product: RecommendationsProduct;
    recommendationTypes: ReturnType<typeof getRecommendationTypes>;
}) {
    const meta = recommendationTypes[product.type as keyof typeof recommendationTypes];
    return (
        <Stack direction='row' spacing={0.5} justifyContent='space-between'>
            <Stack direction='row' spacing={0.5} alignItems='center'>
                {meta?.icon}
                <Typography color='primary' variant='subtitle1'>
                    {meta?.name}
                </Typography>
            </Stack>
            <Typography fontWeight={700} color='primary' variant='subtitle1'>
                {product.name}
            </Typography>
        </Stack>
    );
}

function EmptyProductPlaceholders() {
    return (
        <>
            {EMPTY_PLACEHOLDER_KEYS.map((key) => (
                <Typography key={key} color='primary' variant='subtitle1'>
                    {'-'}
                </Typography>
            ))}
        </>
    );
}

export const RecommendationsDashboardCards = ({
    hybridRecommendations,
    cropProtectionRecommendations,
    isValidating,
    showEmptyPlaceholders = false,
}: {
    hybridRecommendations: UserNodeRecommendationsDashboardResponse | undefined;
    cropProtectionRecommendations: UserNodeRecommendationsDashboardResponse | undefined;
    isValidating: boolean;
    showEmptyPlaceholders?: boolean;
}) => {
    const recommendationTypes = getRecommendationTypes();

    const hybridProducts = useMemo(
        () => hybridRecommendations?.mostRecommendedProducts?.slice(0, MAX_PRODUCTS_SHOWN) ?? [],
        [hybridRecommendations?.mostRecommendedProducts]
    );
    const cropProtectionProducts = useMemo(
        () => cropProtectionRecommendations?.mostRecommendedProducts?.slice(0, MAX_PRODUCTS_SHOWN) ?? [],
        [cropProtectionRecommendations?.mostRecommendedProducts]
    );

    const renderProductList = (
        products: RecommendationsProduct[],
        recTypes: ReturnType<typeof getRecommendationTypes>
    ) =>
        products.length > 0 ? (
            products.map((product) => (
                <MostRecommendedProductRow
                    key={`${product.type}-${product.name}`}
                    product={product}
                    recommendationTypes={recTypes}
                />
            ))
        ) : showEmptyPlaceholders ? (
            <EmptyProductPlaceholders />
        ) : null;

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr', xs: '1fr' }, gap: 2 }}>
            <DashboardCard
                title={_('Recomendaciones de híbrido')}
                isLoading={isValidating}
                number={hybridRecommendations?.count ?? 0}
                spacing={2}
            >
                <Typography color='primary' variant='subtitle2' fontWeight='bold'>
                    {_('Productos más recomendados')}
                </Typography>
                <Stack direction='column' spacing={0.5}>
                    {renderProductList(hybridProducts, recommendationTypes)}
                </Stack>
            </DashboardCard>
            <DashboardCard
                title={_('Recomendaciones de Protección de Cultivos')}
                isLoading={isValidating}
                number={cropProtectionRecommendations?.count ?? 0}
                spacing={2}
            >
                <Typography color='primary' variant='subtitle2' fontWeight='bold'>
                    {_('Productos más recomendados')}
                </Typography>
                <Stack direction='column' spacing={0.5}>
                    {renderProductList(cropProtectionProducts, recommendationTypes)}
                </Stack>
            </DashboardCard>
        </Box>
    );
};
